import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Open/close state for a dropdown that should close on outside click or Escape.
 *
 * The Quasar screens did this with `document.getElementById(...)
 * .classList.toggle('show')` plus a global click listener registered in
 * `created()` and never removed — one per component instance, all still live
 * after navigation. This returns a ref to attach to the dropdown root instead.
 */
export function useDismissable<T extends HTMLElement = HTMLDivElement>(initial = false) {
  const [isOpen, setIsOpen] = useState(initial)
  const ref = useRef<T>(null)

  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((open) => !open), [])
  const open = useCallback(() => setIsOpen(true), [])

  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  return { ref, isOpen, open, close, toggle } as const
}
